/**
 * Approval-rules API client (Milestone 4-B, spec §3.11).
 *
 * Wraps gateway endpoints in apps/chaos-gateway/src/approval-rules.ts:
 *   GET    /api/orgs/:orgId/approval-rules
 *   POST   /api/orgs/:orgId/approval-rules
 *   PATCH  /api/approval-rules/:id
 *   DELETE /api/approval-rules/:id
 *
 * Decision grammar:
 *   auto_approve | auto_reject | require_human_any
 *   require_role:<slug>
 *   require_user:<uuid>
 */
import { apiJson } from './client';

export interface ApprovalRule {
  id: string;
  orgId: string;
  scopeCardId: string | null;
  scopeColumnId: string | null;
  scopeRoleId: string | null;
  scopeBoardId: string | null;
  scopeProjectId: string | null;
  trigger: string;
  decision: string;
  conditions: unknown;
  priority: number;
  active: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateApprovalRuleInput {
  scopeCardId?: string | null;
  scopeColumnId?: string | null;
  scopeRoleId?: string | null;
  scopeBoardId?: string | null;
  scopeProjectId?: string | null;
  trigger: string;
  decision: string;
  conditions?: unknown;
  priority?: number;
  active?: boolean;
}

export interface UpdateApprovalRuleInput {
  trigger?: string;
  decision?: string;
  conditions?: unknown;
  priority?: number;
  active?: boolean;
}

export interface ListApprovalRulesParams {
  active?: boolean;
  trigger?: string;
}

interface ApprovalRulesListResponse {
  rules: ApprovalRule[];
}
interface ApprovalRuleResponse {
  rule: ApprovalRule;
}

function buildQuery(params: ListApprovalRulesParams = {}): string {
  const qs = new URLSearchParams();
  if (params.active === true) qs.set('active', 'true');
  if (params.active === false) qs.set('active', 'false');
  if (params.trigger) qs.set('trigger', params.trigger);
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export const approvalRulesApi = {
  list(orgId: string, params: ListApprovalRulesParams = {}): Promise<ApprovalRule[]> {
    return apiJson<ApprovalRulesListResponse>(
      `/orgs/${orgId}/approval-rules${buildQuery(params)}`,
    ).then((r) => r.rules);
  },
  create(orgId: string, input: CreateApprovalRuleInput): Promise<ApprovalRule> {
    return apiJson<ApprovalRuleResponse>(`/orgs/${orgId}/approval-rules`, {
      method: 'POST',
      json: input,
    }).then((r) => r.rule);
  },
  update(id: string, input: UpdateApprovalRuleInput): Promise<ApprovalRule> {
    return apiJson<ApprovalRuleResponse>(`/approval-rules/${id}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.rule);
  },
  remove(id: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(`/approval-rules/${id}`, { method: 'DELETE' });
  },
};

export const approvalRulesKeys = {
  all: ['approval-rules'] as const,
  list: (orgId: string, params: ListApprovalRulesParams = {}) =>
    [...approvalRulesKeys.all, 'list', orgId, params] as const,
};

// Common triggers (autocomplete hints). Mirrors the backend trigger
// executor + approval engine; safe-list, not exhaustive.
export const KNOWN_TRIGGERS: string[] = [
  'card.create',
  'card.archive',
  'card.unarchive',
  'card.assign',
  'card.move',
  'card.workflow.transition',
  'sub_task.create',
  'sub_task.update',
  'sub_task.complete',
  'sub_task.delete',
  'comment.create',
  'role_message.create',
  'role_message.respond',
  'artifact.create',
  'artifact.delete',
  'approval.request',
  'session.start',
  'session.stop',
  'connector.invoke',
];
